import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, X, Star, Zap } from 'lucide-react';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { supabase } from '../../lib/supabase';
import { useNotifications } from '../../contexts/NotificationContext';
import BottomNavigation from '../../components/Layout/BottomNavigation';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import type { Profile } from '../../lib/supabase';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, isAuthenticated } = useSupabaseAuth();
  const { sendNotification } = useNotifications();
  const [potentialMatches, setPotentialMatches] = useState<Profile[]>([]);
  const [currentProfileIndex, setCurrentProfileIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  // Load potential matches
  useEffect(() => {
    if (user && profile) {
      loadPotentialMatches();
    }
  }, [user, profile]);

  const loadPotentialMatches = async () => {
    if (!user || !profile) return;
    
    setLoading(true);
    try {
      // Get users that haven't been liked by current user
      const { data: likedUsers, error: likedError } = await supabase
        .from('likes')
        .select('to_user_id')
        .eq('from_user_id', user.id);

      if (likedError) {
        console.error('Error fetching liked users:', likedError);
      }

      const likedUserIds = likedUsers?.map(like => like.to_user_id) || [];

      // Get potential matches based on preferences
      const { data: matches, error } = await supabase
        .from('profiles')
        .select('*')
        .neq('user_id', user.id)
        .eq('is_active', true)
        .gte('age', profile.min_age)
        .lte('age', profile.max_age)
        .not('user_id', 'in', likedUserIds.length > 0 ? `(${likedUserIds.join(',')})` : '(null)')
        .limit(10);

      if (error) throw error;

      setPotentialMatches(matches || []);
      setCurrentProfileIndex(0);
    } catch (error) {
      console.error('Error loading potential matches:', error);
      setPotentialMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = async (direction: 'left' | 'right') => {
    if (!user || potentialMatches.length === 0) return;

    const currentMatch = potentialMatches[currentProfileIndex];
    
    if (direction === 'right') {
      // Like the user
      try {
        // Create like
        const { error: likeError } = await supabase
          .from('likes')
          .insert([{
            from_user_id: user.id,
            to_user_id: currentMatch.user_id,
            is_super: false
          }]);

        if (likeError) throw likeError;

        // Check if it's a mutual like
        const { data: mutualLike } = await supabase
          .from('likes')
          .select('*')
          .eq('from_user_id', currentMatch.user_id)
          .eq('to_user_id', user.id)
          .single();
        
        if (mutualLike) {
          // Create match
          await supabase
            .from('matches')
            .insert([{
              user1_id: user.id,
              user2_id: currentMatch.user_id,
              is_active: true
            }]);

          // Create conversation
          await supabase
            .from('conversations')
            .insert([{
              user1_id: user.id,
              user2_id: currentMatch.user_id,
              is_active: true
            }]);

          sendNotification(
            'πPi Dating - New Match! 💕',
            `It's a match with ${currentMatch.first_name}! Start chatting now.`,
            'match'
          );
          alert(`It's a match with ${currentMatch.first_name}! 💕`);
          setTimeout(() => {
            navigate('/matches');
          }, 1000);
        } else {
          sendNotification(
            'πPi Dating - Like Sent! ❤️',
            `You liked ${currentMatch.first_name}!`,
            'like'
          );
        }
      } catch (error) {
        console.error('Error liking user:', error);
      }
    }

    // Move to next profile
    if (currentProfileIndex < potentialMatches.length - 1) {
      setCurrentProfileIndex(prev => prev + 1);
    } else {
      // Load more matches
      await loadPotentialMatches();
    }
  };

  const handleSuperLike = async () => {
    if (!user || potentialMatches.length === 0) return;

    const currentMatch = potentialMatches[currentProfileIndex];
    
    try {
      // Create super like
      const { error: likeError } = await supabase
        .from('likes')
        .insert([{
          from_user_id: user.id,
          to_user_id: currentMatch.user_id,
          is_super: true
        }]);

      if (likeError) throw likeError;

      // Check if it's a mutual like
      const { data: mutualLike } = await supabase
        .from('likes')
        .select('*')
        .eq('from_user_id', currentMatch.user_id)
        .eq('to_user_id', user.id)
        .single();
      
      if (mutualLike) {
        // Create match
        await supabase
          .from('matches')
          .insert([{
            user1_id: user.id,
            user2_id: currentMatch.user_id,
            is_active: true
          }]);

        // Create conversation
        await supabase
          .from('conversations')
          .insert([{
            user1_id: user.id,
            user2_id: currentMatch.user_id,
            is_active: true
          }]);

        sendNotification(
          'πPi Dating - Super Match! ⭐💕',
          `It's a super match with ${currentMatch.first_name}!`,
          'match'
        );
        alert(`Super match with ${currentMatch.first_name}! ⭐💕`);
        setTimeout(() => {
          navigate('/matches');
        }, 1000);
      } else {
        sendNotification(
          'πPi Dating - Super Like Sent! ⭐',
          `You super liked ${currentMatch.first_name}! They'll be notified.`,
          'superlike'
        );
        alert(`Super liked ${currentMatch.first_name}! ⭐`);
      }

      // Move to next profile
      if (currentProfileIndex < potentialMatches.length - 1) {
        setCurrentProfileIndex(prev => prev + 1);
      } else {
        await loadPotentialMatches();
      }
    } catch (error) {
      console.error('Error super liking user:', error);
    }
  };

  if (!isAuthenticated || !user || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen pb-20 flex items-center justify-center">
        <Card className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Finding your perfect matches...</p>
        </Card>
        <BottomNavigation />
      </div>
    );
  }

  if (potentialMatches.length === 0) {
    return (
      <div className="min-h-screen pb-20">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <img src="/logo 3 BR.png" alt="Pi Dating" className="h-12 w-12" />
          <h1 className="text-2xl font-bold text-white flex items-center gap-1">
            <div className="text-2xl">👤</div>
            <div className="text-xs text-white/70">{profile.first_name}</div>
          </h1>
          <Button
            onClick={() => navigate('/premium')}
            variant="gradient"
            size="sm"
            className="flex items-center gap-1"
          >
            <Zap size={16} />
            Premium
          </Button>
        </div>

        <div className="px-4 flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center">
            <div className="text-6xl mb-4">💕</div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              No More Matches
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              You've seen all available matches in your area. Check back later for new people!
            </p>
            <Button
              onClick={loadPotentialMatches}
              variant="gradient"
              size="lg"
            >
              <Zap size={20} className="mr-2" />
              Refresh Matches
            </Button>
          </Card>
        </div>

        <BottomNavigation />
      </div>
    );
  }

  const currentMatch = potentialMatches[currentProfileIndex];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <img src="/logo 3 BR.png" alt="Pi Dating" className="h-12 w-12" />
        <div className="flex items-center gap-2">
          <div className="text-2xl">🥧</div>
          <div className="text-xs text-white/70">@{profile.first_name}</div>
        </div>
        <h1 className="text-xl font-bold text-white flex items-center gap-1">
          <span className="text-yellow-300">π</span>Pi Dating
        </h1>
        <Button
          onClick={() => navigate('/premium')}
          variant="gradient"
          size="sm"
          className="flex items-center gap-1"
        >
          <Zap size={16} />
          Premium
        </Button>
      </div>

      {/* Main Card */}
      <div className="px-4">
        <Card className="relative h-[600px] overflow-hidden">
          <img
            src={currentMatch.photos[0] || 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=400'}
            alt={currentMatch.first_name}
            className="w-full h-full object-cover"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Profile Info */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-3xl font-bold">{currentMatch.first_name}</h2>
              <span className="text-2xl">({currentMatch.age})</span>
            </div>
            <p className="text-lg opacity-90 mb-3">
              {currentMatch.location_city && currentMatch.location_country 
                ? `${currentMatch.location_city}, ${currentMatch.location_country}`
                : 'Location not specified'
              }
            </p>
            
            {/* Bio */}
            {currentMatch.bio && (
              <p className="text-sm opacity-80 mb-3">{currentMatch.bio}</p>
            )}
            
            {/* Interests */}
            {currentMatch.interests && currentMatch.interests.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {currentMatch.interests.slice(0, 3).map((interest, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm"
                  >
                    {interest}
                  </span>
                ))}
                {currentMatch.interests.length > 3 && (
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                    +{currentMatch.interests.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="absolute bottom-6 right-6 flex gap-3">
            <button
              onClick={() => handleSwipe('left')}
              className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-200 hover:scale-110 border-2 border-white/30 shadow-lg"
            >
              <X size={24} className="drop-shadow-sm" />
            </button>
            <button
              onClick={() => handleSwipe('right')}
              className="w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white hover:shadow-xl transition-all duration-200 hover:scale-110 border-2 border-white/20 shadow-lg hover:from-pink-400 hover:to-purple-500"
            >
              <Heart size={24} className="drop-shadow-sm" fill="currentColor" />
            </button>
            <button
              onClick={handleSuperLike}
              className="w-14 h-14 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white hover:shadow-xl transition-all duration-200 hover:scale-110 border-2 border-white/20 shadow-lg hover:from-yellow-300 hover:to-orange-400"
            >
              <Star size={24} className="drop-shadow-sm" fill="currentColor" />
            </button>
          </div>

          {/* Progress Indicator */}
          <div className="absolute top-4 left-4 right-4">
            <div className="flex gap-1">
              {potentialMatches.slice(0, 5).map((_, index) => (
                <div
                  key={index}
                  className={`flex-1 h-1 rounded-full ${
                    index === currentProfileIndex 
                      ? 'bg-white' 
                      : index < currentProfileIndex 
                      ? 'bg-white/50' 
                      : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
          </div>
        </Card>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default HomePage;